import { useState } from 'react';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import { Link, Tooltip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';
import { useItemBatches } from 'src/hooks/use-item-batches';

import { fDate, fDateTime } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';
import { hasPermission } from 'src/utils/has-permissions';
import { getBatchExpiryStatus } from 'src/utils/batch-expiry-status';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { BarAvailabilityStatus, IBarListItem } from 'src/types/bar';

type Props = {
  selected: boolean;
  onEditRow: VoidFunction;
  row: IBarListItem;
  onDeleteRow: VoidFunction;
};

export default function BarTableRow({ row, selected, onEditRow, onDeleteRow }: Props) {
  const { _id, itemName, quantity, updatedAt, itemCode, unit, availability, price } = row;

  const { user: authUser } = useAuthContext();

  const confirm = useBoolean();

  const popover = usePopover();

  const [isExpanded, setIsExpanded] = useState(false);

  const { batches, status: batchesStatus } = useItemBatches(isExpanded ? _id : null);

  return (
    <>
      <Link
        component={RouterLink}
        href={paths.dashboard.bar.view(_id)}
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'contents',
        }}
      >
        <TableRow hover selected={selected}>
          <TableCell sx={{ width: 48 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsExpanded((prev) => !prev);
              }}
            >
              <Iconify icon={isExpanded ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'} />
            </IconButton>
          </TableCell>

          <TableCell sx={{ display: 'flex', alignItems: 'left' }}>
            <ListItemText
              primary={itemName}
              secondary={itemCode}
              primaryTypographyProps={{ typography: 'body2' }}
              secondaryTypographyProps={{
                component: 'span',
                color: 'text.disabled',
              }}
            />
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {quantity} {unit.abbreviation}
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
            {fCurrencyRupees(price * 100)}
          </TableCell>

          <TableCell sx={{ textAlign: 'left' }}>
            <Label
              variant="soft"
              color={
                (availability === BarAvailabilityStatus.IN_STOCK && 'success') ||
                (availability === BarAvailabilityStatus.OUT_OF_STOCK && 'error') ||
                (availability === BarAvailabilityStatus.RUNNING_LOW && 'warning') ||
                'default'
              }
            >
              {availability.split('-').join(' ')}
            </Label>
          </TableCell>

          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {fDateTime(updatedAt)}
          </TableCell>

          <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
            <Link component={RouterLink} href={paths.dashboard.bar.view(_id)} mr={2}>
              <Button variant="soft" color="primary">
                View More
              </Button>
            </Link>

            {hasPermission(
              authUser,
              'any',
              AuthPermissions.UPDATE_BAR_ITEM,
              AuthPermissions.DELETE_BAR_ITEM,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Tooltip title="More options">
                <IconButton
                  color={popover.open ? 'inherit' : 'default'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    popover.onOpen(e);
                  }}
                >
                  <Iconify icon="eva:more-vertical-fill" />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      </Link>

      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Stack spacing={1} sx={{ py: 2, px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Batch breakdown
              </Typography>

              {batchesStatus === 'loading' && (
                <Typography variant="body2" color="text.secondary">
                  Loading…
                </Typography>
              )}

              {batchesStatus === 'success' && batches.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No active batches
                </Typography>
              )}

              {batchesStatus === 'success' &&
                batches.map((batch) => {
                  const expiryStatus = getBatchExpiryStatus(batch.expiryDate);
                  const color =
                    (expiryStatus === 'expired' && 'error.main') ||
                    (expiryStatus === 'expiring-soon' && 'warning.main') ||
                    undefined;

                  return (
                    <Stack
                      key={batch._id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ maxWidth: 480 }}
                    >
                      {batch.expiryDate ? (
                        <Typography variant="body2" color={color}>
                          {fDate(batch.expiryDate)}
                        </Typography>
                      ) : (
                        <Label variant="soft" color="default">
                          Unknown Expiry
                        </Label>
                      )}

                      <Typography variant="body2" color={color ?? 'text.secondary'}>
                        {batch.quantity} {unit.abbreviation}
                      </Typography>
                    </Stack>
                  );
                })}
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        {hasPermission(
          authUser,
          'any',
          AuthPermissions.UPDATE_BAR_ITEM,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <MenuItem
            onClick={() => {
              onEditRow();
              popover.onClose();
            }}
          >
            <Iconify icon="solar:pen-bold" />
            Edit
          </MenuItem>
        )}

        {hasPermission(
          authUser,
          'any',
          AuthPermissions.DELETE_BAR_ITEM,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <MenuItem
            onClick={() => {
              confirm.onTrue();
              popover.onClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <Iconify icon="solar:trash-bin-trash-bold" />
            Delete
          </MenuItem>
        )}
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete Bar Item"
        content={`Are you sure want to delete ${itemName}? This action will permenantly delete ${itemName}'s details from the system`}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              confirm.onFalse();
              onDeleteRow();
            }}
          >
            Delete
          </Button>
        }
      />
    </>
  );
}
